import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldPlus, ClipboardCheck, ListChecks, ArrowRight, MapPin, Clock, Check, Truck } from "lucide-react";
import { useAuth, isReadOnly } from "@/context/AuthContext";
import { api, errMessage } from "@/lib/api";
import { fmtDate } from "@/lib/time";
import { PageTitle, Loading, ErrorBox, Empty } from "@/components/common";
import { Take5Card } from "@/components/Cards";
import { EndReminders } from "@/components/EndReminders";

export const ACTIVE_SITE_KEY = "safestart.activeSite";

export const equipmentUrl = (siteId, category) => {
  const p = new URLSearchParams();
  if (category) p.set("categorie", category);
  if (siteId) p.set("site", siteId);
  const s = p.toString();
  return `/equipements${s ? `?${s}` : ""}`;
};

const ACTIONS = [
  { to: () => "/take5/nouveau", title: "Nouveau Take 5", text: "Analyser les risques avant de commencer une tâche.", icon: ShieldPlus, id: "quick-new-take5", primary: true },
  { to: (s) => equipmentUrl(s, "vehicule"), title: "Contrôler un véhicule", text: "Contrôle du véhicule avant utilisation.", icon: Truck, id: "quick-inspect-vehicle" },
  { to: (s) => equipmentUrl(s, "equipement"), title: "Contrôler un équipement", text: "Engin ou équipement : contrôle avant utilisation.", icon: ClipboardCheck, id: "quick-inspect" },
  { to: () => "/activites", title: "Mes activités", text: "Retrouver mes Take 5 et mes inspections.", icon: ListChecks, id: "quick-activities" },
];

const MobileActions = ({ readOnly, siteId }) => (
  <div className="grid gap-4 md:hidden">
    {ACTIONS.filter((a) => !readOnly || a.id === "quick-activities").map((a, idx) => (
      <Link key={a.id} to={a.to(siteId)} data-testid={a.id} style={{ animationDelay: `${idx * 60}ms` }}
        className={`rise group flex min-h-[132px] flex-col justify-between rounded-xl border p-5 transition-colors ${a.primary ? "border-amber-500 bg-amber-500 text-slate-950 hover:bg-amber-400" : "border-border bg-card hover:border-amber-500/50"}`}>
        <a.icon className="h-7 w-7" />
        <div>
          <p className="font-heading text-2xl font-bold uppercase leading-tight">{a.title}</p>
          <p className={`text-sm ${a.primary ? "text-slate-900" : "text-muted-foreground"}`}>{a.text}</p>
        </div>
      </Link>
    ))}
  </div>
);

const Hero = ({ readOnly, siteId }) => (
  <div data-testid="home-hero" className="dark-scope rise relative overflow-hidden rounded-2xl border border-border bg-[#1E2A36]">
    <div className="hazard-stripe h-2" />
    <div className="relative z-10 px-12 py-14">
      <p className="flex items-center gap-2 font-data text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
        <span className="h-2 w-2 rounded-full bg-emerald-400" /> Avant de commencer
      </p>
      <h2 className="mt-5 max-w-2xl font-heading text-5xl leading-[1.05]">
        Prendre cinq minutes <span className="text-amber-400">peut tout changer.</span>
      </h2>
      <p className="mt-5 max-w-lg text-base text-slate-300">Take 5 avant chaque tâche, contrôle des équipements et des véhicules avant utilisation.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        {readOnly ? (
          <Link to="/supervision" data-testid="home-open-supervision" className="inline-flex h-14 items-center gap-3 rounded-lg bg-amber-500 px-7 text-base font-bold text-slate-950 transition-colors hover:bg-amber-400">
            Consulter la supervision <ArrowRight className="h-5 w-5" />
          </Link>
        ) : (<>
        <Link to="/take5/nouveau" data-testid="home-start-take5" className="inline-flex h-14 items-center gap-3 rounded-lg bg-amber-500 px-7 text-base font-bold text-slate-950 transition-colors hover:bg-amber-400">
          <ShieldPlus className="h-5 w-5" /> Démarrer un Take 5 <ArrowRight className="h-5 w-5" />
        </Link>
        <Link to={equipmentUrl(siteId, "vehicule")} data-testid="home-check-vehicle" className="inline-flex h-14 items-center gap-3 rounded-lg border border-slate-500 px-6 text-base font-semibold text-slate-100 transition-colors hover:border-amber-400 hover:text-amber-300">
          <Truck className="h-5 w-5" /> Contrôler un véhicule
        </Link>
        <Link to={equipmentUrl(siteId, "equipement")} data-testid="home-check-equipment" className="inline-flex h-14 items-center gap-3 rounded-lg border border-slate-500 px-6 text-base font-semibold text-slate-100 transition-colors hover:border-amber-400 hover:text-amber-300">
          <ClipboardCheck className="h-5 w-5" /> Contrôler un équipement
        </Link>
        </>)}
      </div>
    </div>
    <div className="pointer-events-none absolute right-16 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full border-[40px] border-amber-500/[0.06]" />
    <div className="pointer-events-none absolute right-28 top-1/2 h-56 w-56 -translate-y-1/2 rounded-full border border-slate-500/30" />
    <div className="absolute bottom-6 right-10 flex items-center gap-3 font-data text-xs text-amber-400" aria-hidden="true">01 <span className="h-px w-16 bg-slate-500" /> 05</div>
  </div>
);

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

export const InfoBar = ({ sites, siteId, setSiteId, counts, idPrefix = "" }) => (
  <div data-testid={`${idPrefix}home-info-bar`} className="mt-3 flex flex-wrap items-center gap-x-10 gap-y-3 rounded-xl border border-border bg-card px-5 py-4 md:px-6">
    <div className="flex min-w-0 items-center gap-3">
      <MapPin className="h-5 w-5 shrink-0 text-amber-400" />
      <div className="min-w-0">
        <label htmlFor={`${idPrefix}active-site`} className="block text-xs text-muted-foreground">Je travaille aujourd'hui sur le site :</label>
        <select id={`${idPrefix}active-site`} data-testid={`${idPrefix}home-active-site`} value={siteId} onChange={(e) => setSiteId(e.target.value)}
          className="max-w-full bg-transparent text-lg font-semibold focus:outline-none [&>option]:bg-card">
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <Clock className="h-5 w-5 shrink-0 text-amber-400" />
      <div className="text-sm">
        <p data-testid={`${idPrefix}home-today-count`} className="font-semibold"><span className="font-data">{counts ? plural(counts.take5s, "Take 5", "Take 5") : "—"}</span> aujourd'hui</p>
        <p data-testid={`${idPrefix}home-today-inspections`} className="font-semibold"><span className="font-data">{counts ? plural(counts.inspections, "inspection", "inspections") : "—"}</span> aujourd'hui</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-x-6 gap-y-2 md:ml-auto">
      <Link to={equipmentUrl(siteId)} data-testid={`${idPrefix}home-find-equipment`} className="flex items-center gap-2 text-sm font-semibold hover:text-amber-300">Trouver un équipement <ArrowRight className="h-4 w-4" /></Link>
      <Link to="/activites" data-testid={`${idPrefix}home-my-activities`} className="flex items-center gap-2 text-sm font-semibold hover:text-amber-300">Mes activités <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </div>
);

const Reminders = ({ questions }) => (
  <div className="mt-10" data-testid="home-reminders">
    <p className="font-data text-xs uppercase tracking-[0.2em] text-muted-foreground">À retenir</p>
    <div className="mt-1 flex items-end justify-between">
      <h2 className="font-heading text-3xl font-bold uppercase">Avant chaque intervention</h2>
      <span className="font-data text-xs uppercase tracking-widest text-muted-foreground">{questions.length} points</span>
    </div>
    <ol className="mt-4 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
      {questions.map((q, i) => (
        <li key={q.id} className="flex items-center gap-4 px-5 py-4">
          <span className="font-data text-xs text-amber-400">{String(i + 1).padStart(2, "0")}</span>
          <span className="flex-1 text-sm">{q.text}</span>
          <Check className="h-4 w-4 text-slate-400" />
        </li>
      ))}
    </ol>
  </div>
);

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [refs, setRefs] = useState(null);
  const [siteId, setSiteIdState] = useState(() => localStorage.getItem(ACTIVE_SITE_KEY) || "");
  const [error, setError] = useState("");

  const load = () => {
    setError("");
    Promise.all([api.get("/activities"), api.get("/referentials")])
      .then(([a, r]) => { setData(a.data); setRefs(r.data); })
      .catch((e) => setError(errMessage(e)));
  };
  useEffect(() => { load(); }, []);

  const setSiteId = (id) => {
    localStorage.setItem(ACTIVE_SITE_KEY, id);
    setSiteIdState(id);
  };
  const sites = refs?.sites || [];
  const activeId = sites.some((s) => s.id === siteId) ? siteId : sites[0]?.id || "";
  const [counts, setCounts] = useState(null);
  useEffect(() => { if (activeId && activeId !== siteId) localStorage.setItem(ACTIVE_SITE_KEY, activeId); }, [activeId, siteId]);
  useEffect(() => {
    if (!activeId) return;
    api.get("/activities/today", { params: { site_id: activeId } }).then((r) => setCounts(r.data)).catch(() => {
      if (!data) return setCounts(null);
      const now = new Date().toISOString();
      const today = (at, site) => at && fmtDate(at, site) === fmtDate(now, site);
      setCounts({
        take5s: data.take5s.filter((t) => t.site?.id === activeId && t.status !== "brouillon" && !t.superseded_by && today(t.start_at, t.site)).length,
        inspections: data.inspections.filter((i) => i.site?.id === activeId && i.status === "finalisee" && today(i.finalized_at, i.site)).length,
      });
    });
  }, [activeId, data]);
  const current = data?.take5s.filter((t) => t.status === "brouillon" || t.status === "autorisee").slice(0, 4) || [];

  return (
    <div>
      <div className="md:hidden"><PageTitle kicker={user.org_name} title={`Bonjour ${user.name.split(" ")[0]}`} testId="home-title" /></div>
      <div className="hidden md:block"><PageTitle kicker="Votre journée" title="Prêt pour le terrain ?" testId="home-title-desktop" /></div>
      {data && <EndReminders take5s={data.take5s} onChange={load} />}
      {refs && sites.length > 0 && (
        <div className="mb-4 md:hidden">
          <InfoBar sites={sites} siteId={activeId} setSiteId={setSiteId} counts={counts} idPrefix="mobile-" />
        </div>
      )}
      <MobileActions readOnly={isReadOnly(user)} siteId={activeId} />
      {error && <ErrorBox message={error} onRetry={load} />}
      {!data && !error && <Loading />}
      {data && refs && (
        <div className="hidden md:block">
          <Hero readOnly={isReadOnly(user)} siteId={activeId} />
          <InfoBar sites={sites} siteId={activeId} setSiteId={setSiteId} counts={counts} />
          <Reminders questions={refs.questions} />
        </div>
      )}
      {data && (
        <div className="mt-10">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide md:text-lg">Take 5 en cours</h2>
            <Link to="/activites" className="flex items-center gap-1 text-sm font-semibold text-amber-400" data-testid="home-see-all">Tout voir <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {!current.length && <Empty testId="home-empty">Aucun Take 5 en cours.</Empty>}
          <div className="grid gap-3 md:grid-cols-2">{current.map((t) => <Take5Card key={t.id} t={t} />)}</div>
        </div>
      )}
    </div>
  );
}
